import {
  Avatar,
  Box,
  Button,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getModalMenu } from "../../../data/menu";
import { Logout } from "@mui/icons-material";
import { useAuthContext } from "../../../hooks/Auth";
import { iUser } from "../../../interfaces/Auth/iUser";
import { environment } from "../../../config";

const { REACT_APP_API_CONTROL_TRANSACCIONAL, ENVIRONMENT, VERSION } = environment;
const baseApiBroker = REACT_APP_API_CONTROL_TRANSACCIONAL;
const proyectVersion = VERSION;


function extractOrganizacion(data: any): string | null {
  if (!data) return null;

  if (typeof data.organizacion === "string" && data.organizacion.trim() !== "") {
    return data.organizacion.trim();
  }

  if (
    data.body &&
    typeof data.body.organizacion === "string" &&
    data.body.organizacion.trim() !== ""
  ) {
    return data.body.organizacion.trim();
  }

  if (
    Array.isArray(data.body) &&
    data.body.length > 0 &&
    typeof data.body[0].organizacion === "string" &&
    data.body[0].organizacion.trim() !== ""
  ) {
    return data.body[0].organizacion.trim();
  }

  if (
    Array.isArray(data) &&
    data.length > 0 &&
    typeof data[0].organizacion === "string" &&
    data[0].organizacion.trim() !== ""
  ) {
    return data[0].organizacion.trim();
  }


  return null;
}

interface Props {
  anchorEl: HTMLElement | null;
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
}

const ProfileModal: FC<Props> = ({ anchorEl, setAnchorEl }) => {
  const navigate = useNavigate();
  const { logout, canAccess, user } = useAuthContext();
  const isOpen = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const modalMenu = getModalMenu(user as iUser);

  const handleRoute = (route: string) => {
    navigate(`/asistente/${route}`);
  };

  const allowedMenu = modalMenu.filter(
    (item) => !item.permission || canAccess(item.permission)
  );

  const [organizacion, setOrganizacion] = useState<string | null>(null);
  const [loadingOrganizacion, setLoadingOrganizacion] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);

  useEffect(() => {
    const idOrganizacion = user?.id_organizacion;
    if (!idOrganizacion) {
      setOrganizacion(null);
      setOrgError(null);
      setLoadingOrganizacion(false);
      return;
    }


    if (!baseApiBroker) {
      setOrgError("API base no configurada");
      setOrganizacion(null);
      return;
    }

    if (!proyectVersion) {
      setOrgError("Versión de proyecto no configurada");
      setOrganizacion(null);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchOrganizacion = async () => {
      setLoadingOrganizacion(true);
      setOrgError(null);

      try {
        const base = String(baseApiBroker).replace(/\/$/, "");

        const url = `${base}/creditos-organizacion?id_organizacion=${encodeURIComponent(
          idOrganizacion
        )}&version=${encodeURIComponent(proyectVersion)}`;


        if (process.env.NODE_ENV === "development") {

          console.debug("[ProfileModal] Fetch URL:", url);
        }

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",

          },
          signal,
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();


        if (process.env.NODE_ENV === "development") {

          console.debug("[ProfileModal] API response:", data);
        }

        const extracted = extractOrganizacion(data);

        if (extracted) {
          setOrganizacion(extracted);
        } else {

          setOrganizacion(null);
          setOrgError("Campo 'organizacion' no disponible en la respuesta");
        }
      } catch (err: any) {
        if (err.name === "AbortError") {

          return;
        }
        setOrganizacion(null);
        setOrgError(err?.message ?? "Error al obtener organización");
      } finally {
        setLoadingOrganizacion(false);
      }
    };

    fetchOrganizacion();
    return () => {
      controller.abort();
    };
  }, [user?.id_organizacion, baseApiBroker, proyectVersion]);

  //Obtener iniciales del usuario
  function getInitials(input?: string | null): string {
    if (!input) return "";
    const base = input.trim();
    // Si viene nombre, usa primeras letras de hasta 2 palabras. Si no, usa parte del email antes de @
    const name = base.includes("@") ? base.split("@")[0] : base;
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  // Color de fondo estable a partir del nombre/email (simple hash → HSL)
  function stringToHsl(input?: string | null, s = 65, l = 45): string | undefined {
    if (!input) return undefined;
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      hash = input.charCodeAt(i) + ((hash << 5) - hash);
      hash |= 0;
    }
    const h = Math.abs(hash) % 360;
    return `hsl(${h} ${s}% ${l}%)`;
  }


  return (
    <>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={isOpen}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            bgcolor: "#FAFAFA",
            borderRadius: 5,
            color: "#336B87",
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            }
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >

        <MenuItem sx={{ mb: 2 }}>
          <Avatar
            src={(user as any)?.avatarUrl || undefined}
            alt={user?.name ?? user?.email ?? "user"}
            sx={{
              width: "40px !important",
              height: "40px !important",
              fontSize: 22,
              color: "#336B87",
              bgcolor: "#90AFC5",
              mr: 1.5,
            }}
          >
            {!(user as any)?.avatarUrl ? getInitials(user?.name || user?.email) : null}
          </Avatar>
          <Stack direction="column" >
            <Typography fontFamily={"Montserrat"} color={"#336B87"} sx={{ fontWeight: 600, fontSize: 16, mb: -0.5 }} > {user?.name}</Typography>
            <Typography fontFamily={"Montserrat"} sx={{ fontSize: 12 }} >{user?.email}</Typography>

          </Stack>
        </MenuItem>

        {/* items dinámicos */}
        {allowedMenu.map((item) => (
          <MenuItem key={item.label} onClick={() => handleRoute(item.route)}
            sx={{

              borderRadius: 3,
              fontFamily: "Montserrat",
              fontWeight: 600,
              transition: "background-color .15s ease, color .15s ease, padding-left .15s ease",
              "&:hover": {
                bgcolor: "#25485A",
                color: "#ffff",
                pl: 2.5,
              },
              "&.Mui-focusVisible": {
                bgcolor: "#25485A",
                color: "#F8AD0C",
              },
              position: "relative",
              "&:hover::before, &.Mui-focusVisible::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 6,
                bottom: 6,
                width: 4,
                borderRadius: 2,
                backgroundColor: "#F8AD0C",
              },
            }}>

            <Box
              component="span"
              sx={{
                width: 22,
                height: 22,
                mr: 1.25,
                bgcolor: "currentColor", // ← toma el color del texto (cambia en hover)
                WebkitMask: `url(${item.icon}) no-repeat center / contain`,
                mask: `url(${item.icon}) no-repeat center / contain`,
                flex: "0 0 auto",
              }}
            />{item.label}
          </MenuItem>
        ))}


        {/* logout */}

        <Box px={1.5} pt={2} pb={1.5} mt={2}>
          <Button
          fullWidth
            variant="contained"
            onClick={() => {
              // cierra el menú y luego sale
              handleClose();
              logout(user as iUser);
            }}
            startIcon={<Logout />}
            sx={{
             
              borderRadius:5,
              fontFamily: "Montserrat",
              fontWeight: 700,
              textTransform: "none",
              bgcolor: "#336B87",          // color base (tu rojo semitransparente)
              color: "#fff",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#ff0000ff",        // rojo sólido al hover
                boxShadow: "none",
              },
              "&:focus-visible": {
                outline: "2px solid #F8AD0C",
                outlineOffset: "2px",
              },
              // Si quieres simular la “barra” izquierda del hover:
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 8,
                top: 8,
                bottom: 8,
                width: 4,
                borderRadius: 2,
                backgroundColor: "transparent",
                transition: "background-color .15s ease",
              },
            }}
          >
            Cerrar sesión
          </Button>
        </Box>
      </Menu>

    </>
  );
};

export default ProfileModal;
